/**
 * EncryptionUtils.js
 * 
 * Pure encryption/decryption utilities using Web Crypto API
 * - AES-GCM 256-bit encryption
 * - PBKDF2 key derivation with 100,000 iterations
 * - Random salt and IV for each encryption
 * - Base64 encoding for storage compatibility
 * 
 * No external dependencies - uses native browser Web Crypto API
 */

/**
 * Derives a cryptographic key from a password using PBKDF2
 * @param {string} password - The encryption password/key
 * @param {Uint8Array} salt - Salt for key derivation (16 bytes)
 * @returns {Promise<CryptoKey>} - The derived AES-GCM key
 */
const deriveKey = async (password, salt) => {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);
  
  // Import password as key material
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );
  
  // Derive actual encryption key using PBKDF2
  // High iteration count (100,000) for security against brute-force attacks
  return await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000, // OWASP recommended minimum
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 }, // 256-bit AES key
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypts a value using AES-GCM encryption
 * 
 * Process:
 * 1. Generate random 16-byte salt
 * 2. Generate random 12-byte IV (initialization vector)
 * 3. Derive 256-bit AES key from password using PBKDF2
 * 4. Encrypt data with AES-GCM
 * 5. Combine: salt + IV + encrypted_data
 * 6. Encode as Base64 for storage
 * 
 * @param {string} value - The value to encrypt
 * @param {string} encryptionKey - The encryption password/key
 * @returns {Promise<string>} - Base64 encoded encrypted value with salt and IV
 */
const encryptValue = async (value, encryptionKey) => {
  if (!encryptionKey) {
    console.warn('EncryptionUtils: No encryption key provided, returning value as-is');
    return value;
  }
  
  if (!value) {
    return value;
  }
  
  try {
    // Generate random salt (16 bytes)
    // Salt ensures the same password produces different keys each time
    const salt = window.crypto.getRandomValues(new Uint8Array(16));
    
    // Generate random initialization vector (12 bytes for AES-GCM)
    // IV ensures the same plaintext encrypts differently each time
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    // Derive encryption key from password + salt
    const key = await deriveKey(encryptionKey, salt);
    
    // Encode the value to bytes
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    
    // Encrypt using AES-GCM (provides both encryption and authentication)
    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      data
    );
    
    // Combine salt + IV + encrypted data into single array
    // Format: [salt(16) | IV(12) | ciphertext(?)]
    const encryptedArray = new Uint8Array(encryptedData);
    const combined = new Uint8Array(salt.length + iv.length + encryptedArray.length);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(encryptedArray, salt.length + iv.length);
    
    // Convert to base64 for storage compatibility
    return arrayBufferToBase64(combined);
  } catch (error) {
    console.error('EncryptionUtils: Encryption failed:', error);
    // Return original value if encryption fails (fallback for compatibility)
    return value;
  }
};

/**
 * Decrypts a value encrypted with encryptValue
 * 
 * Process:
 * 1. Decode Base64 to bytes
 * 2. Extract salt (first 16 bytes)
 * 3. Extract IV (next 12 bytes)
 * 4. Extract encrypted data (remaining bytes)
 * 5. Derive same key from password + salt
 * 6. Decrypt with AES-GCM
 * 7. Return plaintext
 * 
 * @param {string} encryptedValue - Base64 encoded encrypted value
 * @param {string} encryptionKey - The encryption password/key
 * @returns {Promise<string|null>} - The decrypted value, or null if decryption fails
 */
const decryptValue = async (encryptedValue, encryptionKey) => {
  if (!encryptionKey) {
    console.warn('EncryptionUtils: No encryption key provided, returning value as-is');
    return encryptedValue;
  }
  
  if (!encryptedValue) {
    return encryptedValue;
  }
  
  try {
    // First, check if the value looks like it could be base64
    // If it fails base64 decode, it's probably plaintext
    let combined;
    try {
      combined = base64ToArrayBuffer(encryptedValue);
    } catch (base64Error) {
      console.warn('EncryptionUtils: Value is not valid base64 - returning as plaintext');
      return encryptedValue; // Return as plaintext
    }
    
    // Validate minimum length (16 bytes salt + 12 bytes IV + at least 17 bytes encrypted data with auth tag)
    if (combined.length < 45) {
      console.warn('EncryptionUtils: Data too short to be encrypted - returning as plaintext');
      return encryptedValue;
    }
    
    // Extract salt (first 16 bytes)
    const salt = combined.slice(0, 16);
    
    // Extract IV (next 12 bytes)
    const iv = combined.slice(16, 28);
    
    // Extract encrypted data (remaining bytes - includes 16-byte auth tag)
    const encryptedData = combined.slice(28);
    
    // Derive the same key from password and salt
    const key = await deriveKey(encryptionKey, salt);
    
    // Decrypt using AES-GCM
    // GCM mode automatically verifies the authentication tag
    const decryptedData = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encryptedData
    );
    
    // Decode the decrypted data
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('EncryptionUtils: Decryption failed:', error.name);
    console.error('EncryptionUtils: This usually means old plaintext data exists. Clear storage with: sessionStorage.clear()');
    // Return null to indicate decryption failure
    return null;
  }
};

/**
 * Checks if a value appears to be encrypted
 * @param {string} value - The value to check
 * @returns {boolean} - True if value appears to be encrypted
 */
const isEncrypted = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  
  try {
    // Try to decode as base64
    const decoded = base64ToArrayBuffer(value);
    // Encrypted values should be at least 45 bytes (16 salt + 12 IV + 17 min encrypted)
    return decoded.length >= 45;
  } catch (error) {
    return false;
  }
};

/**
 * Converts ArrayBuffer to Base64 string
 * @param {Uint8Array} buffer - The buffer to convert
 * @returns {string} - Base64 encoded string
 */
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  
  return window.btoa(binary);
};

/**
 * Converts Base64 string to Uint8Array
 * @param {string} base64 - Base64 encoded string
 * @returns {Uint8Array} - The decoded buffer
 */
const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  return bytes;
};

// Export encryption functions
export {
  encryptValue,
  decryptValue,
  deriveKey,
  isEncrypted,
  arrayBufferToBase64,
  base64ToArrayBuffer,
};

// Export for browser testing (window.EncryptionUtils)
if (typeof window !== 'undefined') {
  window.EncryptionUtils = {
    encryptValue,
    decryptValue,
    deriveKey,
    isEncrypted,
    arrayBufferToBase64,
    base64ToArrayBuffer,
  };
}
